import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { PAGE_HEADER_TITLE_SX } from '../constants/page-header';
import PageTitleIcon from './PageTitleIcon';

export interface PageHeaderProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Small label above the title (e.g. "ESG intelligence"). */
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  titleSx?: SxProps<Theme>;
  titleComponent?: React.ElementType;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Editorial page header — sits directly on the content canvas (no card):
 * icon square, title, subtitle, trailing actions; tabs (children) below on a
 * full-width hairline.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  eyebrow,
  actions,
  onBack,
  backLabel,
  titleSx,
  titleComponent = 'h1',
  children,
  sx,
}) => {
  const backButton = onBack ? (
    <IconButton
      onClick={onBack}
      aria-label={backLabel ?? 'Back'}
      size="small"
      sx={{
        flexShrink: 0,
        color: 'text.secondary',
        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        // Back arrow points to the start edge in both directions.
        '[dir="rtl"] & svg': { transform: 'scaleX(-1)' },
      }}
    >
      <ArrowBackIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <Box
      component="header"
      sx={[
        {
          mb: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          gap: children ? 2 : 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexWrap: 'wrap',
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 320px' }}>
          {backLabel && backButton ? <Tooltip title={backLabel}>{backButton}</Tooltip> : backButton}
          {icon ? <PageTitleIcon icon={icon} /> : null}
          <Box sx={{ minWidth: 0 }}>
            {eyebrow ? (
              <Typography variant="overline" component="p" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              variant="h4"
              component={titleComponent}
              sx={[{ ...PAGE_HEADER_TITLE_SX, m: 0 }, ...(Array.isArray(titleSx) ? titleSx : titleSx ? [titleSx] : [])]}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '72ch' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              flexShrink: 0,
              maxWidth: '100%',
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
      {/* minmax(0, 1fr): the tab row's intrinsic width (sum of all tabs) must not
          widen the shell on phones; the scrollable Tabs handle the overflow. */}
      {children ? <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>{children}</Box> : null}
    </Box>
  );
};

export default PageHeader;
